import React, { useState, useEffect } from 'react'

import CustomNumericKeypad from './numeric-keypad'
import apis from 'browser/app/models/apis'
import { isPhoneValid } from 'shared-libs/helpers/utils'

import { IKioskComponentProps, IKioskContext } from './checkin'
import { KioskNextButton, KioskInputField } from '../utils'

import './_driver-checkin.scss'

interface IDriverCheckInProps extends IKioskComponentProps {
  context: IKioskContext & { phoneNumber: string }
  setTitle: (title: string) => void
}

/**
 * Kiosk driver checkin view.  Lookup the driver based on phone number.
 */
const DriverCheckIn: React.FC<IDriverCheckInProps> = ({ context, onNext, setTitle }) => {
  const [phoneNumber, setPhoneNumber] = useState(context?.phoneNumber)
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    setTitle('Driver Sign In')
  }, [setTitle])

  const handleKeyPress = (key: string) => {
    setPhoneNumber((currentNumber) => {
      if (currentNumber.length < 10) {
        return currentNumber + key
      }
      return currentNumber
    })
  }

  const handleBackspace = () => {
    setPhoneNumber((currentNumber) => currentNumber.slice(0, -1))
  }

  const handleNext = async () => {
    setIsLoading(true)
    try {
      const user = await apis.fetchUserByIdentity(phoneNumber)
      let firm = null
      if (user) {
        const firmId = user.get('owner.firm.entityId')
        if (firmId) {
          firm = await user.getStore().getOrFetchRecord(firmId)
        }
      }
      onNext({ phoneNumber, user, firm })
    } catch (error) {
      console.error('Failed to fetch user or firm:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const formatPhoneNumber = (num: string): string => {
    const digits = num.slice(0, 10)
    if (digits.length === 0) return ''
    if (digits.length <= 3) return `(${digits}`
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
  }

  const isNextDisabled = !isPhoneValid(phoneNumber) || isLoading

  return (
    <>
      <h2 className="kiosk-instructions-text">
        Enter your phone number, instructions will be provided via text message
      </h2>
      <div className="kiosk-phone-input-wrapper">
        <KioskInputField
          autoFocus
          readOnly
          placeholder="Enter your cell phone number"
          iconName="phone"
          iconSize={'40'}
          type="tel"
          value={formatPhoneNumber(phoneNumber)}
          style={{
            textAlign: 'center',
          }}
        />
      </div>

      <div className="kiosk-keypad-container">
        <CustomNumericKeypad onKeyPress={handleKeyPress} onBackspace={handleBackspace} />
      </div>

      <KioskNextButton onClick={handleNext} isDisabled={isNextDisabled} isLoading={isLoading}>
        CONTINUE
      </KioskNextButton>
    </>
  )
}

export default DriverCheckIn
